// pages/alarm/BatteryWarningDetail.js —— 报警中心 · 电池预警详情（doc01 2.11 第 2 张图）
// 顶部「返回」+「电池预警详情」面板（右侧展示最新诊断时间）；
// 信息区：站点名称 / 装机功率·容量 / 投运日期（一行）+ 电站地址（独占一行）；
// 「诊断结论」六个维度各自带状态指示；「诊断明细」表格（预警类型 + 6 个维度列，行为 原因分析 / 运维建议 / 各 BMS 单体行），
// 点击单体行展示该单体的温度 / 电压曲线；支持导出。
import React, { useState } from 'react';
import { Table, Button, Modal, message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import {
  ArrowLeftOutlined, DownloadOutlined, RiseOutlined, FallOutlined, ThunderboltOutlined,
  WarningOutlined, DisconnectOutlined, BugOutlined, ClockCircleOutlined,
} from '@ant-design/icons';
import {
  getBatteryWarnDetail, getBatteryCurves, BATTERY_WARN_TYPES, BATTERY_WARN_REASON, BATTERY_WARN_ADVICE,
} from '../../config/stationData';

const TYPE_ICON = {
  socHigh: <RiseOutlined />, socLow: <FallOutlined />, microShort: <ThunderboltOutlined />,
  lowCapacity: <WarningOutlined />, badConn: <DisconnectOutlined />, bmsCollect: <BugOutlined />,
};

const DOT_COLOR = { 正常: '#52e8b0', 关注: '#f4c542', 异常: '#ff5a7f' };

const TICK = { fill: 'rgba(255,255,255,0.5)', fontSize: 11 };
const TIP = { background: '#0d1f44', border: '1px solid rgba(0,180,216,0.35)', borderRadius: 8, color: '#fff' };

const mapKeys = (src) => BATTERY_WARN_TYPES.reduce((acc, t) => { acc[t.key] = src[t.key]; return acc; }, {});
const slashKeys = () => BATTERY_WARN_TYPES.reduce((acc, t) => { acc[t.key] = '/'; return acc; }, {});

const BatteryWarningDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const d = getBatteryWarnDetail(id || 'hn');

  // 点击诊断明细中的单体行 → 展示该单体温度 / 电压曲线
  const [curveOf, setCurveOf] = useState(null);
  const curves = curveOf ? getBatteryCurves(curveOf.seed) : null;

  const detailRows = [
    { key: 'reason', label: '原因分析', ...mapKeys(BATTERY_WARN_REASON) },
    { key: 'advice', label: '运维建议', ...mapKeys(BATTERY_WARN_ADVICE) },
    ...d.bmsRows.map((b, i) => ({ key: b.key, label: b.name, sub: b.sub, seed: i + 1, ...slashKeys() })),
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/app/alarm/battery')}>返回</Button>
      </div>

      <div className="panel">
        <div className="panel-title" style={{ justifyContent: 'space-between' }}>
          <span>电池预警详情</span>
          <span className="diag-time"><ClockCircleOutlined style={{ marginRight: 6 }} />最新诊断时间：{d.diagAt}</span>
        </div>

        <div className="diag-info-table">
          <div className="diag-info-row">
            <span className="diag-info-label">站点名称</span>
            <span className="diag-info-value">{d.station}</span>
            <span className="diag-info-label">装机功率/容量</span>
            <span className="diag-info-value">{d.power}</span>
            <span className="diag-info-label">投运日期</span>
            <span className="diag-info-value">{d.onlineAt}</span>
          </div>
          <div className="diag-info-row diag-info-row-wide">
            <span className="diag-info-label">电站地址</span>
            <span className="diag-info-value diag-info-wide">{d.address}</span>
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-title">诊断结论</div>
        <div className="diag-conclusions">
          {BATTERY_WARN_TYPES.map((t) => {
            const st = (d.conclusion && d.conclusion[t.key]) || '正常';
            return (
              <div className="diag-conclusion" key={t.key}>
                <span className="diag-conclusion-icon">{TYPE_ICON[t.key]}</span>
                <span className="diag-conclusion-label">
                  <i style={{ background: DOT_COLOR[st] }} />{t.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="panel">
        <div className="panel-title" style={{ justifyContent: 'space-between' }}>
          <span>诊断明细</span>
          <Button size="small" icon={<DownloadOutlined />} onClick={() => message.success('诊断明细已导出')}>导出</Button>
        </div>
        <Table
          rowKey="key"
          dataSource={detailRows}
          size="middle"
          pagination={{ pageSize: 8, size: 'small' }}
          scroll={{ x: 1200 }}
          rowClassName={(r) => (r.seed ? 'diag-bms-row' : '')}
          onRow={(r) => (r.seed ? { onClick: () => setCurveOf({ name: r.label, sub: r.sub, seed: r.seed }) } : {})}
          columns={[
            {
              title: '预警类型', dataIndex: 'label', width: 150, strong: true,
              render: (v, r) => (r.seed ? (
                <span className="diag-bms-cell"><b>{v}</b><small>{r.sub}</small></span>
              ) : v),
            },
            ...BATTERY_WARN_TYPES.map((t) => ({ title: t.label, dataIndex: t.key, width: 176 })),
          ]}
        />
      </div>

      <Modal
        className="diag-curve-modal"
        title={`${curveOf ? `${curveOf.name}（${curveOf.sub}）` : ''} · 单体温度 / 电压曲线`}
        open={!!curveOf}
        onCancel={() => setCurveOf(null)}
        width={760}
        footer={null}
      >
        {curves ? (
          <>
            <div className="panel-title">单体温度<small>单位：℃</small></div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={curves.temp} margin={{ top: 6, right: 16, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="t" tick={TICK} axisLine={false} tickLine={false} interval={3} />
                <YAxis tick={TICK} axisLine={false} tickLine={false} domain={['dataMin - 1', 'dataMax + 1']} />
                <Tooltip contentStyle={TIP} />
                <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }} />
                <Line type="monotone" dataKey="temp" name="温度(℃)" stroke="#f4c542" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
            <div className="panel-title" style={{ marginTop: 16 }}>单体电压<small>单位：V</small></div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={curves.volt} margin={{ top: 6, right: 16, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="t" tick={TICK} axisLine={false} tickLine={false} interval={3} />
                <YAxis tick={TICK} axisLine={false} tickLine={false} domain={['dataMin - 0.01', 'dataMax + 0.01']} />
                <Tooltip contentStyle={TIP} />
                <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }} />
                <Line type="monotone" dataKey="volt" name="电压(V)" stroke="#00d4ff" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </>
        ) : null}
      </Modal>
    </div>
  );
};

export default BatteryWarningDetail;
